import { useEffect, useState } from 'react'
import { Check, Loader2, RefreshCw, Lock } from 'lucide-react'
import { api } from '@/shared/api/client'
import { explain } from '@/shared/api/problem'
import { Panel, SectionHeader, StatusBadge } from '@/components/erp/primitives'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from './hooks'
import { useCan } from '@/shared/auth/session'
import { cn } from '@/lib/utils'
import type { SettingResponse, SettingsByCategory } from '@/shared/api/types'

/**
 * `GET /settings` values are JSON-encoded STRINGS on the wire (`"Crowbyt Axis"`,
 * `true`, `72`), whatever `valueType` says. We parse them for display and let
 * `api.put` re-encode the native value (the request wrapper JSON.stringify's it,
 * which is exactly the raw-JSON body `PUT /settings/{key}` expects).
 */
function parseWire(value?: string | null): unknown {
  if (value == null || value === '') return null
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/**
 * Check a value against the definition's own `validation` blob before sending it.
 *
 * The point is not to duplicate the server's rules — the server still enforces them and
 * still wins. It is that the first feedback on a typo should not be a 400 after the value
 * has already been PUT. `app.web-url` is the case that taught this: a wrong URL there is
 * a link in somebody else's inbox, and it saved silently.
 *
 * Anything this does not understand passes. An unknown validation key is the backend
 * being newer than the bundle, and refusing to save on that basis would be worse than
 * letting the server judge.
 */
export function settingProblem(s: SettingResponse, value: string): string | null {
  const type = s.valueType?.toUpperCase() ?? 'STRING'
  if (value === '') return null                    // clearing is how you fall back

  let rules: Record<string, unknown> = {}
  try { rules = s.validation ? JSON.parse(s.validation) as Record<string, unknown> : {} } catch { /* server judges */ }

  if (type === 'INTEGER' || type === 'DECIMAL') {
    const n = Number(value)
    if (Number.isNaN(n)) return 'Must be a number.'
    if (typeof rules.min === 'number' && n < rules.min) return `Must be at least ${rules.min}.`
    if (typeof rules.max === 'number' && n > rules.max) return `Must be at most ${rules.max}.`
    return null
  }

  if (rules.format === 'url' || s.key.endsWith('-url') || s.key.endsWith('.url')) {
    let u: URL
    try { u = new URL(value) } catch { return 'Must be a full URL, including https://' }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'Only http:// and https:// are allowed.'
    if (u.username || u.password) return 'Credentials do not belong in this URL.'
    if (u.search || u.hash) return 'No query string or #fragment — this is a base URL.'
    if (value.endsWith('/')) return 'Drop the trailing slash.'
    return null
  }

  if (rules.format === 'email' || s.key.endsWith('email')) {
    return /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value) ? null : 'Does not look like an email address.'
  }

  if (typeof rules.pattern === 'string') {
    try {
      if (!new RegExp(rules.pattern).test(value)) return `Must match ${rules.pattern}`
    } catch { /* an un-compilable pattern is the server's problem */ }
  }
  return null
}

/** The editable string form of the current value (secrets never echo). */
function displayValue(s: SettingResponse): string {
  const raw = parseWire(s.value)
  if (s.secret) return ''
  const type = s.valueType?.toUpperCase() ?? 'STRING'
  if (type === 'BOOLEAN') return raw === true ? 'true' : 'false'
  return raw == null ? '' : String(raw)
}

/** Auto-saving control — tracks input and persists on a debounce (secrets on blur). */
function Control({ s, canUpdate, onSaved }: {
  s: SettingResponse
  canUpdate: boolean
  onSaved: () => void
}) {
  const type = s.valueType?.toUpperCase() ?? 'STRING'
  const initial = displayValue(s)
  const editable = canUpdate && !s.readOnly

  const [draft, setDraft] = useState(initial)
  const debounced = useDebounced(draft, 400)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [errText, setErrText] = useState('')

  async function persist(value: string) {
    if (!editable) return
    if (s.secret ? value === '' : value === initial) return
    const problem = settingProblem(s, value)
    if (problem) { setStatus('error'); setErrText(problem); return }
    setStatus('saving')
    setErrText('')
    try {
      let body: unknown = value
      if (type === 'BOOLEAN') body = value === 'true'
      else if (type === 'INTEGER') body = parseInt(value, 10)
      else if (type === 'DECIMAL') body = parseFloat(value)
      if ((type === 'INTEGER' && Number.isNaN(body)) || (type === 'DECIMAL' && Number.isNaN(body))) {
        setStatus('idle')
        return
      }
      await api.put(`/settings/${s.key}`, body)
      setStatus('saved')
      if (s.secret) setDraft('')
      onSaved()
    } catch (err) {
      setStatus('error')
      setErrText(explain(err))
    }
  }

  /**
   * Numbers and booleans auto-save on a debounce. **Text does not.**
   *
   * A 400ms debounce on a free-text field PUTs every pause in someone's typing, so
   * `https://axis.crowbyt.com` is saved as `https://axis`, then `https://axis.zur`,
   * and each one is a live value in between. For a URL that ends up in other people's
   * inboxes that is not a cosmetic problem. Text commits on blur or Enter — the moment
   * the person has finished saying what they meant.
   */
  const autoSaves = !s.secret && (type === 'INTEGER' || type === 'DECIMAL' || type === 'BOOLEAN')
  useEffect(() => {
    if (!autoSaves || !editable || debounced === initial) return
    void persist(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  const liveProblem = draft !== initial ? settingProblem(s, draft) : null

  const control = s.secret ? (
    <div className="flex items-center gap-2">
      <input
        type="password"
        className="erp-input w-full"
        placeholder={s.value ? 'Set — enter a new value' : 'Not set'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== '' && persist(draft)}
        disabled={!editable}
      />
      <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </div>
  ) : type === 'BOOLEAN' ? (
    <input
      type="checkbox"
      checked={draft === 'true'}
      onChange={(e) => setDraft(e.target.checked ? 'true' : 'false')}
      disabled={!editable}
      className="h-4 w-4 accent-chocolate"
    />
  ) : (
    <input
      type={type === 'INTEGER' || type === 'DECIMAL' ? 'number' : 'text'}
      step={type === 'DECIMAL' ? '0.01' : '1'}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (!autoSaves) void persist(draft) }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !autoSaves) { e.preventDefault(); void persist(draft) }
      }}
      disabled={!editable}
      aria-invalid={liveProblem ? true : undefined}
      className={cn('erp-input w-full', liveProblem && 'border-red-400')}
    />
  )

  return (
    // `data-setting` is the handle the browser tests grab. Twelve identical text boxes
    // on one screen — plus the shell's own search field — make "the first input" a
    // selector that silently tests the wrong control.
    <div className="flex items-center gap-2" data-setting={s.key}>
      <div className="flex-1 min-w-0">{control}</div>
      {status === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
      {status === 'saved' && <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />}
      {status === 'error' && <span className="text-erp-xs text-red-600 shrink-0">{errText}</span>}
      {status !== 'error' && liveProblem && (
        <span className="text-erp-xs text-amber-700 dark:text-amber-400 shrink-0">{liveProblem}</span>
      )}
      {status === 'idle' && !liveProblem && !autoSaves && draft !== initial && (
        <span className="text-erp-xs text-muted-foreground shrink-0">Enter to save</span>
      )}
    </div>
  )
}

/**
 * Where the value in force actually comes from.
 *
 * With an environment fallback under a setting, "the box is empty" and "nothing is
 * configured" stop being the same statement — and only one of them is a problem. This
 * says which, in four words, next to the value.
 */
function SourceBadge({ s }: { s: SettingResponse }) {
  if (!s.source) return null
  const copy: Record<string, { label: string; tone: string; title: string }> = {
    VALUE: { label: 'set here', tone: 'text-muted-foreground', title: 'Stored in the database by an administrator.' },
    ENVIRONMENT: {
      label: 'from the environment', tone: 'text-sky-700 dark:text-sky-400',
      title: 'No value is stored here; the API is using the environment variable behind this setting. Setting a value here overrides it.',
    },
    DEFAULT: { label: 'default', tone: 'text-muted-foreground', title: 'Nobody has changed it.' },
    UNSET: {
      label: 'not configured', tone: 'text-red-700 dark:text-red-400',
      title: 'No value here, nothing in the environment, and no default. Anything that needs this will fail.',
    },
  }
  const c = copy[s.source]
  if (!c) return null
  return (
    <span className={cn('text-erp-xs border border-border rounded-md px-1.5 py-0.5', c.tone)} title={c.title}>
      {c.label}
    </span>
  )
}

export function SystemSettingsTab() {
  const canUpdate = useCan('settings:update')
  // GET /settings returns Map<category, SettingResponse[]> — grouped, not flat.
  const { data, error, loading, reload, refresh } = useLoad(() => api.get<SettingsByCategory>('/settings'), [])
  // Alphabetical, except that APPLICATION comes first: it holds the URL every email
  // links to, and it is the first thing anybody sets up in a new deployment.
  const categories = Object.keys(data ?? {}).sort((a, b) => {
    if (a.toUpperCase() === 'APPLICATION') return -1
    if (b.toUpperCase() === 'APPLICATION') return 1
    return a.localeCompare(b)
  })

  return (
    <div className="space-y-4">
      <SectionHeader
        title="System Settings"
        description="Runtime configuration. Values equal to their default are marked as such."
        right={<button className="erp-btn" onClick={reload}><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>}
      />
      {error ? <div className="erp-card px-4 py-3 text-erp-sm text-red-700 bg-red-50/60">{error}</div> : null}
      {loading ? (
        <div className="erp-card px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading settings…
        </div>
      ) : categories.length === 0 ? (
        <Panel><div className="text-erp-sm text-muted-foreground">No settings defined.</div></Panel>
      ) : categories.map((cat) => (
        <Panel key={cat} title={cat} bodyClassName="p-0">
          {(data?.[cat] ?? []).map((s) => {
            const isDefault = (s.value ?? null) === (s.defaultValue ?? null)
            return (
              <div key={s.key} className="grid gap-3 px-4 py-3 border-b border-border last:border-b-0 md:grid-cols-[minmax(0,1fr)_300px] items-start">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-erp-md font-medium">{s.displayName}</span>
                    {/* `source` is the better answer when the backend sends it: it
                        distinguishes "empty because nobody set it" from "empty because
                        the environment is supplying it". Fall back to the old
                        default/changed badge when it does not. */}
                    {s.source
                      ? <SourceBadge s={s} />
                      : <StatusBadge status={isDefault ? 'draft' : 'active'} className="capitalize" />}
                    {s.readOnly && <span className="text-erp-xs text-muted-foreground border border-border rounded-md px-1.5 py-0.5">system-managed</span>}
                    {s.requiredAuthority && !canUpdate && (
                      <span className="text-erp-xs text-muted-foreground">
                        needs <code className="font-mono">{s.requiredAuthority}</code>
                      </span>
                    )}
                  </div>
                  <div className="text-erp-xs text-muted-foreground font-mono mt-0.5">{s.key}</div>
                  {s.description && <p className="text-erp-sm text-muted-foreground mt-1 mb-0">{s.description}</p>}
                  {s.source === 'ENVIRONMENT' && s.effectiveValue && (
                    <p className="text-erp-xs text-sky-700 dark:text-sky-400 mt-1 font-mono break-all">
                      in force: {s.effectiveValue}
                    </p>
                  )}
                  {s.source === 'UNSET' && (
                    <p className="text-erp-xs text-red-700 dark:text-red-400 mt-1">
                      Nothing is configured — anything that needs this will fail until it is.
                    </p>
                  )}
                </div>
                <Control s={s} canUpdate={canUpdate} onSaved={refresh} />
              </div>
            )
          })}
        </Panel>
      ))}
    </div>
  )
}
